// frontend/src/chunkedUpload.js
//
// 分段續傳上傳。
//
// 為什麼：手機傳一支 2GB 的影片要十幾分鐘，中間只要螢幕暗掉、切換基地台、
// 或系統把分頁丟到背景，XHR 就整條斷掉——而 XHR 沒有斷點，重試等於從第 0 個
// 位元組重來，實測上大檔在手機網路下幾乎不可能一次傳完。
//
// 改成 init → 逐塊 PUT → complete：斷了只補沒送成功的那幾塊。
// upload_id 與已傳進度存在 localStorage，使用者重新整理後再選同一個檔案，
// 就直接從缺的那一塊接下去。

import axios from 'axios';

const PENDING_KEY = 'whisper_pending_upload';
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30000;

// ── 未完成上傳的本機記錄 ─────────────────────
export function loadPendingUpload() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && data.upload_id ? data : null;
  } catch (e) {
    return null;   // 無痕模式或被封鎖：當作沒有紀錄，重新開始就好
  }
}

export function savePendingUpload(info) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(info));
  } catch (e) {
    // 存不下就算了，只是少了續傳能力，主流程不受影響
  }
}

export function clearPendingUpload() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // 同上
  }
}

// name／size／lastModified 三個都一樣才敢當成同一個檔案。
// 認錯檔會把兩份不同內容的片段串在一起，那比重傳一次糟糕得多。
export function pendingMatchesFile(pending, file) {
  if (!pending || !file) return false;
  return pending.name === file.name &&
    pending.size === file.size &&
    pending.lastModified === file.lastModified;
}

// ── 小工具 ───────────────────────────────────
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const backoffMs = (attempt) =>
  Math.min(RETRY_MAX_MS, RETRY_BASE_MS * Math.pow(2, Math.max(0, attempt - 1)));

// 斷網時先等 online 事件，不要在沒有網路的情況下空轉把退避次數燒光。
// 也用輪詢當保險：部分 WebView 的 online 事件不可靠。
function waitForOnline(shouldStop) {
  if (navigator.onLine !== false) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('online', finish);
      clearInterval(poll);
      resolve();
    };
    const poll = setInterval(() => {
      if (navigator.onLine !== false || shouldStop()) finish();
    }, 1000);
    window.addEventListener('online', finish);
  });
}

function canceledError() {
  const e = new Error('已取消上傳');
  e.canceled = true;
  return e;
}

/**
 * 分段上傳一個檔案，回傳 upload_id。
 *
 * onProgress({ loaded, total })：loaded 是「已確認收到」的位元組數
 * onEvent(event, data)：診斷日誌（upload_init／upload_resume／upload_chunk_retry／upload_complete）
 * shouldStop()：回 true 就中止（使用者按了重置）
 */
export async function uploadFileInChunks({
  apiBase = '',
  file,
  onProgress = () => {},
  onEvent = () => {},
  shouldStop = () => false,
}) {
  const stopIfCanceled = () => {
    if (shouldStop()) throw canceledError();
  };

  let uploadId = null;
  let chunkSize = 0;
  let totalChunks = 0;
  let received = new Set();

  const bytesOf = (i) =>
    (i === totalChunks - 1 ? file.size - i * chunkSize : chunkSize);

  const confirmedBytes = () => {
    let sum = 0;
    received.forEach((i) => { sum += bytesOf(i); });
    return Math.min(file.size, sum);
  };

  const report = (extra = 0) =>
    onProgress({ loaded: Math.min(file.size, confirmedBytes() + extra), total: file.size });

  const remember = () => savePendingUpload({
    upload_id: uploadId,
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    chunk_size: chunkSize,
  });

  const init = async () => {
    stopIfCanceled();
    const res = await axios.post(`${apiBase}/upload/init`, {
      filename: file.name,
      size: file.size,
    });
    stopIfCanceled();
    uploadId = res.data.upload_id;
    chunkSize = res.data.chunk_size;
    totalChunks = res.data.total_chunks || Math.ceil(file.size / chunkSize);
    received = new Set(res.data.received || []);
    remember();
    onEvent('upload_init', {
      upload_id: uploadId, size: file.size, chunk_size: chunkSize, chunks: totalChunks,
    });
  };

  // 續傳：拿本機記的 upload_id 去問伺服器還缺哪幾塊。
  // 伺服器那邊的暫存有 TTL，過期了就當作沒這回事，重新 init。
  const tryResume = async () => {
    const pending = loadPendingUpload();
    if (!pendingMatchesFile(pending, file)) return false;
    try {
      const res = await axios.get(`${apiBase}/upload/status/${pending.upload_id}`);
      stopIfCanceled();
      const st = res.data || {};
      if (st.size !== file.size) return false;
      uploadId = pending.upload_id;
      chunkSize = st.chunk_size;
      totalChunks = st.total_chunks;
      received = new Set(st.received || []);
      remember();
      onEvent('upload_resume', {
        upload_id: uploadId, received: received.size, chunks: totalChunks,
      });
      return true;
    } catch (e) {
      if (e.canceled) throw e;
      return false;
    }
  };

  const refreshReceived = async () => {
    try {
      const res = await axios.get(`${apiBase}/upload/status/${uploadId}`);
      received = new Set((res.data && res.data.received) || []);
      report();
    } catch (e) {
      // 問不到就沿用本機紀錄繼續補，最差就是多傳一塊
    }
  };

  const sendChunk = async (index) => {
    let attempt = 0;
    for (;;) {
      stopIfCanceled();
      const start = index * chunkSize;
      const blob = file.slice(start, Math.min(start + chunkSize, file.size));
      try {
        await axios.put(`${apiBase}/upload/chunk/${uploadId}/${index}`, blob, {
          headers: { 'Content-Type': 'application/octet-stream' },
          onUploadProgress: (e) => report(e.loaded || 0),
        });
        stopIfCanceled();
        received.add(index);
        report();
        return;
      } catch (err) {
        if (err.canceled) throw err;
        const status = err.response && err.response.status;
        // 400／413 是我們自己算錯了，重試幾次都一樣
        if (status === 400 || status === 413) throw err;

        attempt += 1;
        onEvent('upload_chunk_retry', { index, attempt, code: err.code || status || 'unknown' });
        await waitForOnline(shouldStop);
        stopIfCanceled();
        await delay(backoffMs(attempt));
        stopIfCanceled();

        if (status === 404 || status === 410) {
          // 伺服器上的暫存被清掉了，只能整份重來
          await init();
          return;
        }
        // 重試前先問清楚已經收到哪幾塊，只補真的還缺的
        await refreshReceived();
        if (received.has(index)) return;
      }
    }
  };

  if (!(await tryResume())) await init();
  report();

  let completeAttempt = 0;
  for (;;) {
    stopIfCanceled();

    let next = -1;
    for (let i = 0; i < totalChunks; i += 1) {
      if (!received.has(i)) { next = i; break; }
    }
    if (next >= 0) {
      await sendChunk(next);
      continue;
    }

    try {
      const res = await axios.post(`${apiBase}/upload/complete/${uploadId}`);
      stopIfCanceled();
      clearPendingUpload();
      onEvent('upload_complete', { upload_id: uploadId, chunks: totalChunks });
      return res.data.upload_id;
    } catch (err) {
      if (err.canceled) throw err;
      const status = err.response && err.response.status;
      const missing = err.response && err.response.data && err.response.data.missing;
      if (status === 409 && Array.isArray(missing)) {
        // 伺服器說還缺這幾塊（或大小對不上）→ 回頭補，不要整份重傳
        for (let k = 0; k < missing.length; k += 1) received.delete(missing[k]);
        onEvent('upload_chunk_retry', { index: missing[0], attempt: 0, code: 'complete_409' });
        report();
        continue;
      }
      if (status === 404 || status === 410) { await init(); continue; }
      if (status === 400 || status === 413) throw err;
      completeAttempt += 1;
      onEvent('upload_chunk_retry', {
        index: -1, attempt: completeAttempt, code: err.code || status || 'unknown',
      });
      await waitForOnline(shouldStop);
      stopIfCanceled();
      await delay(backoffMs(completeAttempt));
    }
  }
}
